// src/hooks/usePrograms.js
// ─────────────────────────────────────────────────────────────
// Central data hook. All Firestore queries for programs live here.
// Components never talk to Firestore directly — they use this hook.
//
// Covers: DS-01 DS-02 DS-03 DS-04 DS-05
// ─────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback } from 'react';
import {
  collection, query, where, orderBy,
  getDocs, addDoc, updateDoc, doc, serverTimestamp,
} from 'firebase/firestore';
import { db, track, EVENTS } from '../lib/firebase';

// ─── Main programs hook ───────────────────────────────────────
export function usePrograms(filters = {}) {
  const [programs, setPrograms]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  const {
    searchTerm   = '',
    category     = 'all',
    province     = 'all',
    freeOnly     = false,       // DS-05
    noReferral   = false,       // DS-05
    familyOK     = false,       // DS-04
    personas     = [],          // Wizard carry-through (WZ-03)
    challenges   = [],          // Wizard carry-through (WZ-03)
  } = filters;

  const fetchPrograms = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Build Firestore query — only filter what Firestore supports natively.
      // Complex filtering (text search, array membership) happens client-side below.
      let q = query(
        collection(db, 'programs'),
        where('active', '==', true),
        orderBy('title')
      );

      // Province filter — Firestore can handle this natively
      // National programs always included regardless of province filter
      // NOTE: We fetch all active programs and filter client-side for province
      // because Firestore doesn't support OR queries elegantly.
      // At 118-500 programs this is fine. Revisit at 10,000+.

      const snapshot = await getDocs(q);
      let results = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      // ── Client-side filtering ──────────────────────────────

      // Province (DS-03): include National + exact match
      if (province !== 'all') {
        results = results.filter(p =>
          p.province === 'National' || p.province === province
        );
      }

      // Category (DS-02)
      if (category !== 'all') {
        results = results.filter(p => p.category === category);
      }

      // Family accessible (DS-04)
      if (familyOK) {
        results = results.filter(p => p.familyOK === true);
      }

      // Free only (DS-05)
      if (freeOnly) {
        results = results.filter(p =>
          p.cost.toLowerCase().includes('free')
        );
      }

      // No referral needed (DS-05)
      if (noReferral) {
        results = results.filter(p =>
          p.referral.toLowerCase().includes('no referral')
        );
      }

      // Keyword search (DS-01) — searches title, org, desc, tags
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase().trim();
        results = results.filter(p =>
          p.title.toLowerCase().includes(term) ||
          p.org.toLowerCase().includes(term) ||
          p.desc.toLowerCase().includes(term) ||
          (p.tags || []).some(t => t.toLowerCase().includes(term))
        );
        track(EVENTS.SEARCH, { term, result_count: results.length });
      }

      // ── Wizard personalisation (WZ-03, PL-05) ────────────
      // Tag each result with a match score so matched results float up.
      // Score 2 = persona AND challenge match
      // Score 1 = either match
      // Score 0 = no match (shown but not highlighted)
      if (personas.length > 0 || challenges.length > 0) {
        results = results.map(p => {
          const personaMatch   = personas.length === 0  || personas.some(pe  => (p.personas  || []).includes(pe));
          const challengeMatch = challenges.length === 0 || challenges.some(ch => p.category === ch);
          const matchScore     = (personaMatch ? 1 : 0) + (challengeMatch ? 1 : 0);
          return { ...p, matchScore };
        });

        // Sort: matched first, then alphabetical within each tier
        results.sort((a, b) => {
          if (b.matchScore !== a.matchScore) return b.matchScore - a.matchScore;
          return a.title.localeCompare(b.title);
        });
      }

      setPrograms(results);
    } catch (err) {
      console.error('[usePrograms] Firestore error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, category, province, freeOnly, noReferral, familyOK,
      // Stringify arrays for stable comparison
      JSON.stringify(personas), JSON.stringify(challenges)]);

  useEffect(() => { fetchPrograms(); }, [fetchPrograms]);

  return { programs, loading, error, refetch: fetchPrograms };
}

// ─── Flag a listing (GV-03) ───────────────────────────────────
export async function flagProgram({ programId, programTitle, reason, details, reporterEmail }) {
  await addDoc(collection(db, 'flags'), {
    programId,
    programTitle,
    reason,
    details: details || '',
    reporterEmail: reporterEmail || '',
    status: 'pending',        // 'pending' | 'reviewed' | 'resolved'
    createdAt: serverTimestamp(),
  });
  track(EVENTS.PROGRAM_FLAGGED, { programId });
}

// ─── Submit a new program (CM-01) ────────────────────────────
export async function submitProgram(formData) {
  await addDoc(collection(db, 'submissions'), {
    ...formData,
    status: 'pending',        // 'pending' | 'approved' | 'rejected'
    createdAt: serverTimestamp(),
  });
  track(EVENTS.PROGRAM_SUBMITTED);
}
